/* eslint-disable eqeqeq */
import isPlainObject from 'lodash/isPlainObject';

import MomentDate from 'atg/utils/MomentDate';
import * as validators from 'atg/utils/validators';

// ---------------------------------------- Data Object Base Class ----------------------------------------

export default class BaseDataObject {
  constructor(data) {
    this.data = data || {};
  }

  invalidate() {
    this.data.invalidated = Date.now();
    //d( 'invalidate', JSON.stringify( this.data ) );
  }

  validate(untilTime) {
    validators.validateData(this.data, untilTime);
  }

  isInvalid() {
    return validators.isDataInvalid(this.data);
  }

  has(name) {
    return name in this.data && this.data[name] !== null;
  }

  isEmpty(callback) {
    return validators.dataIsEmpty(this.data, callback);
  }

  getInteger(name, def) {
    return name in this.data ? Number.parseInt(this.data[name], 10) : def;
  }

  setInteger(name, v) {
    let value = Number.parseInt(v, 10);
    if (Number.isNaN(value)) {
      value = 0;
    }
    this.data[name] = value;
    return value;
  }

  getFloat(name, def) {
    return name in this.data ? Number.parseFloat(this.data[name], 10) : def;
  }

  setFloat(name, v) {
    let value = Number.parseFloat(v);
    if (Number.isNaN(value)) {
      value = 0;
    }
    this.data[name] = value;
    return value;
  }

  getFloatArray(name) {
    const v = this.data[name] || [];
    const a = [];
    for (const [n, element] of v.entries()) {
      a[n] = Number.parseFloat(element);
    }
    return a;
  }

  setFloatArray(name, v) {
    const value = v || [];
    const a = [];
    for (const [n, element] of value.entries()) {
      a[n] = Number.parseFloat(element);
    }
    this.data[name] = a;
    return a;
  }

  getBoolean(name, def) {
    return name in this.data
      ? this.data[name] != false
      : def != null
        ? def
        : false;
  }

  setBoolean(name, v) {
    const value = v != false;
    this.data[name] = value;
    return value;
  }

  getString(name, def) {
    return name in this.data ? String(this.data[name]) : def;
  }

  setString(name, v) {
    const value = v == null ? v : String(v);
    this.data[name] = value;
    return value;
  }

  getDate(name, def) {
    if (name in this.data) {
      const v = this.data[name];

      return v ? new MomentDate(v) : def;
    } else {
      return def;
    }
  }

  setDate(name, v) {
    const value =
      v instanceof MomentDate || v instanceof Date ? v.getTime() : v;
    this.data[name] = value;
    return value;
  }

  getNumberOf(name) {
    return this.data[name]?.length ?? 0;
  }

  getObject(name, cls, index) {
    let data = name ? this.data[name] : this.data;

    if (data) {
      if (index != null) {
        data = data[index];
      }

      if (data) {
        return new cls(data);
      }
    }
  }

  getObjects(name, cls) {
    const data = name ? this.data[name] : this.data;
    const a = [];
    if (data) {
      for (const [n, datum] of data.entries()) {
        a[n] = new cls(datum);
      }
    }
    return a;
  }

  addObject(name, cls, data) {
    if (!this.data[name]) {
      this.data[name] = [];
    }

    const value = data || {};

    this.data[name].push(value);

    return new cls(value);
  }

  deleteObject(name, index) {
    if (this.data[name]) {
      if (index == null) {
        delete this.data[name];
      } else {
        this.data[name].splice(index, 1);
      }
    }
  }

  deleteAllObjects(name) {
    if (this.data[name] && this.data[name].constructor === Array) {
      const arrayLength = this.data[name].length;
      this.data[name].splice(0, arrayLength);
    }
  }

  setObject(name, data, index) {
    const value = data instanceof BaseDataObject ? data.data : data;

    if (index == null) {
      this.data[name] = value;
    } else {
      let a;

      if (name in this.data) {
        a = this.data[name];
      } else {
        a = Number.isNaN(index)
          ? (this.data[name] = {})
          : (this.data[name] = []);
      }

      a[index] = value;
    }
  }

  setObjects(name, arr) {
    const a = (this.data[name] = []);

    for (const v of arr) {
      const data = v instanceof BaseDataObject ? v.data : v;
      if (isPlainObject(data)) {
        a.push(data);
      }
    }
  }

  ensureArray(name) {
    if (!(name in this.data)) {
      this.data[name] = [];
    }
  }

  ensureInteger(name, def) {
    if (!(name in this.data)) {
      this.data[name] = def != null ? def : 0;
    }
  }

  ensureString(name, def) {
    if (!(name in this.data)) {
      this.data[name] = def != null ? Number.parseInt(def, 10) : '';
    }
  }

  getIntegerArray(name) {
    const v = this.data[name] || [];
    const a = [];
    for (const [n, element] of v.entries()) {
      a[n] = Number.parseInt(element, 10);
    }
    return a;
  }

  setIntegerArray(name, v) {
    const value = v || [];
    const a = [];
    for (const [n, element] of value.entries()) {
      a[n] = Number.parseInt(element, 10);
    }
    this.data[name] = a;
    return a;
  }

  addStringArray(name, v) {
    if (Number.isNaN(v)) {
      return v;
    }

    const a = name in this.data ? this.data[name] : (this.data[name] = []);

    if (!a.includes(v)) {
      a.push(v.toString());
    }

    return v;
  }

  getIntegerAt(name, index, def) {
    let v;

    if (name in this.data) {
      v = this.data[name][index];
      v = Number.parseInt(v, 10);

      if (Number.isNaN(v)) {
        v = undefined;
      }
    }

    if (v == null) {
      v = def;
    }

    return v;
  }

  setIntegerAt(name, index, v) {
    const value = Number.parseInt(v, 10);
    const a = name in this.data ? this.data[name] : (this.data[name] = []);

    a[index] = value;

    return value;
  }

  getFloatAt(name, index, def) {
    let v;

    if (name in this.data) {
      v = this.data[name][index];
      v = Number.parseFloat(v);

      if (Number.isNaN(v)) {
        v = undefined;
      }
    }

    if (v == null) {
      v = def;
    }

    return v;
  }

  setFloatAt(name, index, v) {
    const value = Number.parseFloat(v);
    const a = name in this.data ? this.data[name] : (this.data[name] = []);

    a[index] = value;

    return value;
  }

  deleteAt(name, index) {
    if (name in this.data) {
      const a = this.data[name];
      if (a.splice) {
        a.splice(index, 1);
      }
    }
  }

  getStringArray(name) {
    const v = this.data[name] || [];
    const a = [];
    for (const [n, element] of v.entries()) {
      a[n] = element;
    }
    return a;
  }

  getBooleanAt(name, index, def) {
    let v;

    if (name in this.data) {
      v = this.data[name][index];
    }

    if (v == null) {
      v = def;
    }

    return v;
  }

  setBooleanAt(name, index, v) {
    const value = !!v;
    const a = name in this.data ? this.data[name] : (this.data[name] = []);

    a[index] = value;

    return value;
  }

  getArray(name) {
    this.ensureArray(name);
    return this.data[name];
  }
}
